/**
 * The puzzle: where the anchor sits on the keypad and how to count from it to the answer.
 * Matches the Level 3 book's puzzle format: anchor → key → key (count N forward/back).
 */
import { ROUTES, anchorsFor } from "./curriculum";

export interface Puzzle {
  a: number;
  b: number;
  /** The anchor fact used (1, 5, 10 or 11), or null when b is 0 / no route. */
  anchorFact: number | null;
  anchorValue: number | null;
  anchorName: "first" | "middle" | "end" | "eleventh" | null;
  /** Keys from the anchor to the answer, anchor first. Length 1 when b is itself the anchor. */
  keys: number[];
  answerKey: number | null;
  steps: number;
  dir: "forward" | "back";
  /** Book-style caption, e.g. "45 → 4 → 3 (count 2 forward)". */
  caption: string;
}

export const routeFlat = (table: number): number[] | null => {
  const r = ROUTES[table];
  return r ? r.flat() : null;
};

/** The key the k-th fact ends on (its ones digit), by position in the route. */
export function keyForFact(table: number, k: number): number | null {
  const flat = routeFlat(table);
  if (!flat) return null;
  return flat[(k - 1) % flat.length]!;
}

export function nearestAnchorFact(a: number, b: number): number | null {
  const an = anchorsFor(a);
  if (b === 0) return null;
  if (an.includes(b)) return b;
  let best = an[0]!;
  for (const x of an) if (Math.abs(b - x) < Math.abs(b - best)) best = x;
  return best;
}

const anchorName = (n: number | null): Puzzle["anchorName"] => (n === 1 ? "first" : n === 5 ? "middle" : n === 10 ? "end" : n === 11 ? "eleventh" : null);

export function puzzleFor(a: number, b: number): Puzzle {
  const n = nearestAnchorFact(a, b);
  const flat = routeFlat(a);
  const dir: Puzzle["dir"] = n !== null && b < n ? "back" : "forward";
  if (n === null || !flat) {
    return { a, b, anchorFact: n, anchorValue: n === null ? null : a * n, anchorName: anchorName(n), keys: [], answerKey: null, steps: n === null ? 0 : Math.abs(b - n), dir, caption: "" };
  }
  const steps = Math.abs(b - n);
  const keys: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const k = dir === "forward" ? n + i : n - i;
    keys.push(keyForFact(a, k)!);
  }
  const answerKey = keys[keys.length - 1]!;
  const caption = steps === 0 ? "" : `${a * n} → ${keys.slice(1).join(" → ")} (count ${steps} ${dir})`;
  return { a, b, anchorFact: n, anchorValue: a * n, anchorName: anchorName(n), keys, answerKey, steps, dir, caption };
}
